import type { ImInvestorDocument } from '@fruitful/types';
import { useCallback, useEffect, useRef } from 'react';

import { requestBuilder } from '../../http/RequestBuilder';
import { FORMER_MEMBER_API } from '../api/FormerMemberApi';

const FIFTY_FIVE_MINUTES_IN_MS = 55 * 60 * 1000;

export const useFormerMemberInvestmentDocuments = (accountId: string) => {
    const queryResult = useGetInvestmentDocuments(
        { accountId },
        { enabled: accountId !== '' },
    );

    const lastFetchTimeRef = useRef<number | null>(null);
    const previousAccountIdRef = useRef<string | null>(null);

    useEffect(() => {
        if (
            previousAccountIdRef.current !== null &&
            previousAccountIdRef.current !== accountId
        ) {
            lastFetchTimeRef.current = null;
        }
        previousAccountIdRef.current = accountId;
    }, [accountId]);

    if (queryResult.data && lastFetchTimeRef.current === null) {
        lastFetchTimeRef.current = Date.now();
    }

    if (!queryResult.data) {
        lastFetchTimeRef.current = null;
    }

    // Apex signed urls expire after 1 hour. Refresh before download so the link is still valid.
    const getDocumentDownloadUrl = useCallback(
        async (document: ImInvestorDocument): Promise<string> => {
            const now = Date.now();
            const initialFetchTime = lastFetchTimeRef.current;

            if (
                initialFetchTime !== null &&
                now - initialFetchTime >= FIFTY_FIVE_MINUTES_IN_MS
            ) {
                const refetchResult = await queryResult.refetch();
                lastFetchTimeRef.current = Date.now();

                const updatedDocuments = refetchResult.data;
                if (updatedDocuments) {
                    const allDocuments = [
                        ...(updatedDocuments.statements ?? []),
                        ...(updatedDocuments.tradeConfirmations ?? []),
                        ...(updatedDocuments.taxDocuments ?? []),
                    ];
                    const updatedDocument = allDocuments.find(
                        (doc) => doc.name === document.name,
                    );
                    if (updatedDocument) {
                        return updatedDocument.downloadLink;
                    }
                }
            }

            return document.downloadLink;
        },
        [queryResult],
    );

    return {
        ...queryResult,
        getDocumentDownloadUrl,
    };
};

const useGetInvestmentDocuments = requestBuilder.query<
    {
        taxDocuments: ImInvestorDocument[];
        statements: ImInvestorDocument[];
        tradeConfirmations: ImInvestorDocument[];
    } | null,
    { accountId: string }
>({
    endpoint: ({ accountId }) =>
        `${FORMER_MEMBER_API}/investments/${accountId}/documents`,
    queryKey: ({ accountId }) => `formerMemberInvestmentDocuments/${accountId}`,
});
